<template>
	<div class="view-multi-format">
		<!-- Keep spinner, modal, tool, and fallback in one v-if chain so the not-available copy cannot flash while loading. -->
		<LoadingSpinner
			v-if="isLoading && !isEventNotAccessible"
			class="view-multi-format__loading-spinner"
		/>
		<Modal
			v-else-if="isEventNotAccessible"
			modal-name="MultiFormatEventNotAccessibleModal"
			type="dialog"
			theme="dark"
			:closeable="false"
		>
			<template #title>
				{{ $I18n.trans('event.multiformat_editor.event_not_accessible_title') }}
			</template>
			<template #content>
				<p class="view-multi-format__access-denied-message">
					{{ $I18n.trans('event.multiformat_editor.event_not_accessible_message') }}
				</p>
			</template>
			<template #footer>
				<div class="l-button-group">
					<Button
						:text="$I18n.trans('event.multiformat_editor.back_to_homepage')"
						:link="arenaHomeUrl"
						theme="dark"
						modifier="primary"
					/>
				</div>
			</template>
		</Modal>
		<MultiFormatTool
			v-else-if="!isLoading && multiFormatAllowed"
			:event-id="eventId"
			:tag-time="resolvedTagTime"
			:default-graphic-set="resolvedDefaultGraphicSet"
			:graphic-sets="resolvedGraphicSets"
			:event-labels="resolvedEventLabels"
			:event-title="resolvedEventTitle"
			:is-community-cropping="usesCommunityBootstrap"
		/>
		<div
			v-else-if="hasLoadError"
			class="view-multi-format__load-error"
		>
			{{ $I18n.trans('commons.something_went_wrong') }}
		</div>
		<div
			v-else
			class="view-multi-format__not-available"
		>
			{{ $I18n.trans('event.multiformat_editor.not_yet_available') }}
		</div>
	</div>
</template>

<script>
import laroute from '@laroute';
import { httpClient } from '@shared/js/api/httpClient';
import LoadingSpinner from '@shared/components/LoadingSpinner.vue';
import Modal from '@shared/components/Modal';
import Button from '@shared/components/Button';
import MultiFormatTool from '@shared/components/MultiFormatTool/MultiFormatTool.vue';
import Event from '@videotools/js/api/entities/Event';
import ApiClient from '@videotools/js/api';

export default {
	name:       'ViewMultiFormat',
	components: {
		MultiFormatTool,
		LoadingSpinner,
		Modal,
		Button,
	},
	props: {
		eventId: {
			type:     Number,
			required: true,
		},
		eventTagId: {
			type:    [Number, String],
			default: null,
		},
		isCommunityCropping: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			isLoading:                 true,
			hasLoadError:              false,
			isEventNotAccessible:      false,
			resolvedTagTime:           null,
			resolvedGraphicSets:       [],
			resolvedEventLabels:       [],
			resolvedEventTitle:        '',
			resolvedDefaultGraphicSet: null,
			videoAvailable:            false,
		};
	},
	computed: {
		usesCommunityBootstrap() {
			if (this.isCommunityCropping) {
				return true;
			}

			return document.body.classList.contains('is-community-cropping');
		},
		multiFormatAllowed() {
			return this.videoAvailable;
		},
		resolvedEventTagId() {
			if (this.eventTagId) {
				return this.eventTagId;
			}

			const eventTagIdFromQuery = new URLSearchParams(window.location.search).get('eventTagId');

			return eventTagIdFromQuery || null;
		},
		arenaHomeUrl() {
			return laroute.route('arena.home');
		},
	},
	mounted() {
		this.fetchMultiFormatData();
	},
	methods: {
		async fetchMultiFormatData() {
			try {
				if (this.usesCommunityBootstrap) {
					await this.fetchCommunityMultiFormatData();
				} else {
					await this.fetchManagerMultiFormatData();
				}
			} catch (error) {
				console.error(error);
				this.hasLoadError = true;
				this.$toast.error(this.$I18n.trans('commons.something_went_wrong'));
			} finally {
				this.isLoading = false;

				if (this.isEventNotAccessible) {
					this.openEventNotAccessibleModal();
				}
			}
		},
		openEventNotAccessibleModal() {
			this.$nextTick(() => {
				window.eventBus?.$emit('modal:MultiFormatEventNotAccessibleModal');
			});
		},
		resolveVideoAvailable(data) {
			if (typeof data.videoAvailable === 'boolean') {
				return data.videoAvailable;
			}

			if (typeof data.event?.videoAvailable === 'boolean') {
				return data.event.videoAvailable;
			}

			return Boolean(data.event?.streams?.[0]?.videoAvailable);
		},
		async fetchCommunityMultiFormatData() {
			try {
				const response = await httpClient.getCommunityMultiFormat(this.eventId, this.resolvedEventTagId);
				const data = response.data;

				this.resolvedDefaultGraphicSet = data.defaultGraphicSet;
				this.resolvedTagTime = data.tagTime;
				this.resolvedEventLabels = data.event.labels?.map(label => label.name) ?? [];
				this.resolvedEventTitle = data.event.name || '';
				this.videoAvailable = this.resolveVideoAvailable(data);

				await this.hydrateCommunityStore(data.event);
				this.applyCommunityPlayoutAccessError();
			} catch (error) {
				if (this.handleCommunityAccessError(error)) {
					return;
				}

				throw error;
			}
		},
		async fetchManagerMultiFormatData() {
			const data = await ApiClient.getEventMultiFormat(this.eventId, this.resolvedEventTagId);

			this.resolvedGraphicSets = data.graphicSets ?? [];
			this.resolvedTagTime = data.tagTime;
			this.resolvedEventLabels = data.event.labels?.map(label => label.name) ?? [];
			this.resolvedEventTitle = data.event.name || '';
			this.videoAvailable = data.event.videoAvailable ?? false;
		},
		async hydrateCommunityStore(eventData) {
			if (!this.$store) {
				return;
			}

			const wrappedEvent = new Event(eventData);
			await this.$store.dispatch('event/setEvent', wrappedEvent);
			await this.$store.dispatch('event/loadEventTags', { id: wrappedEvent.id });
		},
		// Playout already enforces geoblock, payment, publish window, and similar access rules.
		applyCommunityPlayoutAccessError() {
			if (this.$store?.getters['error/hasPlayoutError']) {
				this.isEventNotAccessible = true;
			}
		},
		handleCommunityAccessError(error) {
			const status = error.response?.status;

			if ([401, 402, 403].includes(status)) {
				this.isEventNotAccessible = true;
				return true;
			}

			return false;
		},
	},
};

</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.view-multi-format {
	height: 100%;
	position: relative;
}

.view-multi-format__loading-spinner {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.view-multi-format__not-available {
	padding: $sp4;
	display: flex;
	align-items: center;
	justify-content: center;
}

.view-multi-format__load-error {
	padding: $sp4;
	display: flex;
	align-items: center;
	justify-content: center;
}

.view-multi-format__access-denied-message {
	margin: 0;
}

</style>
